import React, { useCallback, useState } from "react";
import { useReminders } from "./useReminders";
import ReminderRuleForm from "./ReminderRuleForm";
import ReminderRuleList from "./ReminderRuleList";
import TemplatePreview from "./TemplatePreview";
import "./Reminders.css";

/**
 * Reminders page. Lists the company's reminder rules and lets an admin create a
 * new rule on top of a saved query. Composes the data hook with presentational
 * form/list components — no data logic here.
 * @param {object} props
 * @param {() => void} [props.onOpenQueryBuilder] switches back to the Query
 *   Builder section; the shortcut is hidden when the host provides no way to
 *   navigate.
 * @returns {JSX.Element}
 */
export default function Reminders({ onOpenQueryBuilder }) {
  const { rules, savedQueries, loading, error, create, update, toggle, remove } =
    useReminders();

  /** The rule currently open for editing, or null when creating a new one. */
  const [editingRule, setEditingRule] = useState(null);

  /** The rule whose email is being previewed, or null when none is. */
  const [previewingRule, setPreviewingRule] = useState(null);

  const stopEditing = useCallback(() => setEditingRule(null), []);
  const stopPreviewing = useCallback(() => setPreviewingRule(null), []);

  /**
   * Persists the form — updating the rule being edited, or creating a new one.
   * @param {object} form the rule form state.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const submitRule = useCallback(
    async (form) => {
      if (!editingRule) return create(form);
      const res = await update(editingRule.id, form);
      if (res.ok) setEditingRule(null);
      return res;
    },
    [editingRule, create, update],
  );

  return (
    <div className="rem-root">
      <header className="rem-header">
        <p className="rem-muted">
          Create automated email reminders that fire off a saved query's due
          dates.
        </p>
        {onOpenQueryBuilder && (
          <button
            type="button"
            className="rem-link"
            onClick={onOpenQueryBuilder}
          >
            ← Back to Query Builder
          </button>
        )}
      </header>

      <section className="rem-panel">
        {savedQueries.length === 0 ? (
          <p className="rem-muted">
            No saved queries yet — build and save a query first, then come back
            to create a reminder.
          </p>
        ) : (
          <ReminderRuleForm
            key={editingRule ? editingRule.id : "new"}
            savedQueries={savedQueries}
            initialRule={editingRule}
            onSubmit={submitRule}
            onCancel={editingRule ? stopEditing : undefined}
          />
        )}
      </section>

      <section className="rem-panel">
        <h3 className="rem-subtitle">Reminder rules</h3>
        {error && (
          <div className="rem-error" role="alert">
            {error}
          </div>
        )}
        {loading ? (
          <p className="rem-muted">Loading…</p>
        ) : (
          <ReminderRuleList
            rules={rules}
            savedQueries={savedQueries}
            onEdit={setEditingRule}
            onPreview={setPreviewingRule}
            onToggle={toggle}
            onRemove={remove}
          />
        )}
      </section>

      {previewingRule && (
        <section className="rem-panel">
          <TemplatePreview
            key={previewingRule.id}
            templateKey={previewingRule.templateKey}
            ruleId={previewingRule.id}
            onClose={stopPreviewing}
          />
        </section>
      )}
    </div>
  );
}
